import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Paper,
  Avatar,
  TextField,
  Button,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import type { RunAnalysisEntity, RunStageStep } from '@/types/run-stage.types';
import { useRunAnalysisStore } from '@/run-stage/store/runAnalysisStore';
import { useGraphEditorStore } from '@/graph-store';
import { useSelectedIds } from '@/graph-store/hooks';
import { TranslationGenerator } from '@/components/editor/RightPanel/TranslationPanel/TranslationGenerator';
import { StepTranslationRow } from '@/components/editor/RightPanel/TranslationPanel/StepTranslationRow';
import { InfoTab, type SelectedCell } from '@/components/editor/RightPanel/InfoTab';
import { OperationInfo } from '@/components/editor/RightPanel/InfoTab/OperationInfo';
import { MaterialInfo } from '@/components/editor/RightPanel/InfoTab/MaterialInfo';
import { useOperationCatalog } from '@/api/useOperationCatalog';
import { generateMaterialLabel } from '@/graph/domain/labelGen';
import { filterSnapshotByClone, resolveActiveClone } from '@/graph';
import type { StepIconType, OperationNodeData, GraphSnapshotLight, GraphMaterial } from '@/components/editor/RightPanel/TranslationPanel/translation.types';
import type { OperationTypeDefinition, GraphSnapshot } from '@/graph/domain/types';

interface RunAnalysisSidebarProps {
  analysis: RunAnalysisEntity;
}

type TabName = 'protocol' | 'comments' | 'info';

// Helper function to extract operation ID from visual edge IDs (matches workflow pattern)
const TRANSFER_EDGE_SUFFIXES = ['-transfer', '-remainder', '-dest'] as const;

const extractOperationIdFromEdge = (edgeId: string): string | null => {
  if (!edgeId.startsWith('op-edge-')) return null;

  let operationId = edgeId.substring('op-edge-'.length);
  for (const suffix of TRANSFER_EDGE_SUFFIXES) {
    if (operationId.endsWith(suffix)) {
      operationId = operationId.substring(0, operationId.length - suffix.length);
      break;
    }
  }

  return operationId || null;
};

/**
 * Build a lightweight graph snapshot for TranslationGenerator.
 * Handles both editor graphs and run analysis snapshots which may have different formats.
 */
function buildGraphSnapshot(
  materialNodes: Record<string, any>,
  operationNodes: Record<string, any>,
  flowEdges: Record<string, any>,
  groups: Record<string, any>
): GraphSnapshotLight {
  const materials = new Map<string, GraphMaterial>();

  // Extract material nodes - handle various data formats
  for (const [id, mat] of Object.entries(materialNodes || {})) {
    if (!mat) continue;
    
    const matNode = mat as any;
    
    // Helper to extract scalar value from {value, isChanged} structure or nested values
    const getScalar = (field: any): any => {
      if (field === null || field === undefined) return undefined;
      if (typeof field === 'object' && !Array.isArray(field)) {
        // Check for {value, isChanged} pattern
        if ('value' in field && !('amount' in field) && !('units' in field)) {
          return field.value;
        }
        // For nested objects like container.name, return as-is
        return field;
      }
      return field;
    };

    // Extract container information (handle both nested and flat structures)
    const container = getScalar(matNode.container) || matNode.container || {};
    const containerName = typeof container === 'string' 
      ? container 
      : getScalar(container?.name) || container?.name || '';

    // Extract constituents - can be an array or wrapped
    const constituents = getScalar(matNode.constituents) || matNode.constituents || [];

    materials.set(id, {
      id,
      name: getScalar(matNode.name) || matNode.name || '',
      containerName: containerName || '',
      constituentExpr: generateMaterialLabel(constituents),
      constituents: Array.isArray(constituents) ? constituents : [],
    });
  }

  // Build adjacency from edges: material→operation = input, operation→material = output
  const inputMaterials = new Map<string, string[]>();
  const outputMaterials = new Map<string, string[]>();

  for (const edge of Object.values(flowEdges || {})) {
    const e = edge as any;
    if (!e || !e.sourceId || !e.targetId) continue;
    
    const source = e.sourceId;
    const target = e.targetId;
    const isMaterial = (nodeId: string) => materials.has(nodeId);
    const isOperation = (nodeId: string) => operationNodes[nodeId];

    // Source is material, target is operation → input
    if (isMaterial(source) && isOperation(target)) {
      const list = inputMaterials.get(target) ?? [];
      if (!list.includes(source)) list.push(source);
      inputMaterials.set(target, list);
    }

    // Source is operation, target is material → output
    if (isOperation(source) && isMaterial(target)) {
      const list = outputMaterials.get(source) ?? [];
      if (!list.includes(target)) list.push(target);
      outputMaterials.set(source, list);
    }
  }

  const groupsMap = new Map<string, { label: string; stepIds: string[]; cycleRanges?: Array<{ id: string; startIndex: number; endIndex: number; iterationCount: number }> }>();
  for (const [id, group] of Object.entries(groups || {})) {
    const g = group as any;
    if (!g) continue;
    groupsMap.set(id, {
      label: g.label || '',
      stepIds: g.stepIds || [],
      cycleRanges: g.cycleRanges?.map((cr: any) => ({
        id: cr.id,
        startIndex: cr.startIndex,
        endIndex: cr.endIndex,
        iterationCount: cr.iterationCount,
      })),
    });
  }

  return { materials, inputMaterials, outputMaterials, groups: groupsMap };
}

/**
 * Convert operation node spec to OperationNodeData for TranslationGenerator
 */
/** Unwrap analysis snapshot fields that may be stored as `{ value, isChanged }`. */
function unwrapFieldValue(field: unknown): unknown {
  if (field === null || field === undefined) return field;
  if (typeof field === 'object' && !Array.isArray(field) && 'value' in (field as object)) {
    const keys = Object.keys(field as object);
    // Only unwrap change-tracking wrappers, not quantity-like `{ amount, units }` objects
    if (keys.every((k) => k === 'value' || k === 'isChanged')) {
      return (field as { value: unknown }).value;
    }
  }
  return field;
}

function resolveDataExpected(spec: any): boolean {
  const raw =
    unwrapFieldValue(spec?.dataExpected) ??
    unwrapFieldValue(spec?.parameters?.dataExpected) ??
    unwrapFieldValue(spec?.value?.dataExpected);
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
}

function toOperationNodeData(id: string, spec: any): OperationNodeData {
  return {
    id,
    archetype: spec.archetype || 'TRANSFORM',
    operationTypeId: spec.operationTypeId,
    displayName: spec.displayName || '',
    parameters: spec.parameters || {},
    checkPoints: spec.checkPoints || [],
    duration: spec.duration,
    creationOrder: spec.creationOrder ?? 0,
    groupId: spec.groupId,
    orderInGroup: spec.orderInGroup,
    notes: spec.notes,
    comments: spec.comments,
    dataExpected: resolveDataExpected(spec),
    approvers: spec.approvers,
    notifications: spec.notifications,
    yield: spec.yield,
    transferRow: spec.transferRow,
  };
}

/**
 * ProtocolTab: Displays execution steps using TranslationGenerator
 * Same pattern as graph editor protocol page for consistency
 */
const ProtocolTab: React.FC<{ 
  analysis: RunAnalysisEntity; 
  highlightedStepIds: Set<string>;
  operationNodesMap: Map<string, any>;
  stepIdToGraphNodeId: Map<string, string>;
  graphNodeIdToStepId: Map<string, string>;
  catalog: Map<string, OperationTypeDefinition>;
  snapshot: any;
  materialNodes?: Record<string, any>;
  operationNodes?: Record<string, any>;
  flowEdges?: Record<string, any>;
  groups?: Record<string, any>;
  workspaceId?: string;
}> = ({
  analysis,
  highlightedStepIds,
  operationNodesMap,
  stepIdToGraphNodeId,
  graphNodeIdToStepId,
  catalog,
  snapshot,
  materialNodes: incomingMaterialNodes,
  operationNodes: incomingOperationNodes,
  flowEdges: incomingFlowEdges,
  groups: incomingGroups,
  workspaceId,
}) => {
  const { selectedNodeId, setSelectedNode } = useRunAnalysisStore();
  const { setSelection } = useGraphEditorStore();
  const [activeIconType, setActiveIconType] = useState<StepIconType | null>(null);
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);

  // Build translations using TranslationGenerator (same pattern as graph protocol page)
  const translations = useMemo(() => {
    if (!snapshot || !analysis.executionSteps) return [];

    try {
      // Use passed-in material/operation/edge data, fall back to snapshot extraction
      let rawOperationNodes = incomingOperationNodes || snapshot.operationNodes || {};
      let materialNodes = incomingMaterialNodes || snapshot.materialNodes || {};
      let flowEdges = incomingFlowEdges || snapshot.flowEdges || {};
      let groups = incomingGroups || snapshot.groups || {};
      
      // Convert to object format if it's an array
      if (Array.isArray(rawOperationNodes)) {
        const result: Record<string, any> = {};
        rawOperationNodes.forEach((node: any) => {
          const nodeId = node.id || node.nodeId || node.operationNodeId;
          if (nodeId) result[nodeId] = node;
        });
        rawOperationNodes = result;
      }

      // Build graph snapshot
      const graph = buildGraphSnapshot(materialNodes, rawOperationNodes, flowEdges, groups);

      // Convert operation nodes to OperationNodeData format, sorted by creationOrder
      const operationNodesList = Object.entries(rawOperationNodes)
        .map(([id, spec]) => toOperationNodeData(id, spec as any))
        .sort((a, b) => a.creationOrder - b.creationOrder);

      // Use TranslationGenerator to build translations (pure function, no React deps)
      const generatedTranslations = TranslationGenerator.generateAll(
        operationNodesList,
        graph,
        false, // read-only mode
        catalog
      );

      return generatedTranslations;
    } catch (error) {
      console.error('[ProtocolTab] Failed to generate translations:', error);
      return [];
    }
  }, [snapshot, analysis.executionSteps, catalog, incomingOperationNodes, incomingMaterialNodes, incomingFlowEdges, incomingGroups]);

  // When a step is clicked in protocol, update both analysis store and graph store
  const handleStepClicked = useCallback(
    (stepId: string) => {
      // Update analysis store
      setSelectedNode(stepId);
      
      // Convert nodeId to graphNodeId (operation node)
      const graphNodeId = stepIdToGraphNodeId.get(stepId);
      
      if (graphNodeId) {
        // Select both the operation node and its connected edges
        const cellsToSelect = [
          graphNodeId,
          `op-edge-${graphNodeId}`,
          `op-edge-${graphNodeId}-transfer`,
          `op-edge-${graphNodeId}-remainder`,
          `op-edge-${graphNodeId}-dest`,
        ];
        setSelection(cellsToSelect);
      } else {
        // Fallback: try selecting by nodeId directly
        setSelection([stepId]);
      }
    },
    [setSelectedNode, setSelection, stepIdToGraphNodeId]
  );

  const handleIconClicked = useCallback(
    (nodeId: string, iconType: StepIconType) => {
      // Toggle icon details panel
      // If clicking the same icon again, close it; otherwise open it
      if (activeNodeId === nodeId && activeIconType === iconType) {
        setActiveIconType(null);
        setActiveNodeId(null);
      } else {
        setActiveIconType(iconType);
        setActiveNodeId(nodeId);
      }
    },
    [activeNodeId, activeIconType]
  );

  if (translations.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">No execution steps found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {translations.map((translation) => (
          <StepTranslationRow
            key={translation.nodeId}
            translation={translation}
            highlighted={highlightedStepIds.has(translation.nodeId)}
            highlightedStepIds={highlightedStepIds}
            editable={false}
            activeIconType={activeNodeId === translation.nodeId ? activeIconType : null}
            selectable={false}
            selected={false}
            workspaceId={workspaceId}
            onSelectionToggle={() => {}}
            onStepClicked={handleStepClicked}
            onParameterChanged={() => {}}
            onCheckpointChanged={() => {}}
            onIconClicked={handleIconClicked}
            onEditClicked={() => {}}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * CommentsTab: Display and add comments for selected step
 * EXACT SAME PATTERN as editor's RightPanel Comments tab
 */
const CommentsTab: React.FC<{ 
  analysis: RunAnalysisEntity;
  selectedCell: SelectedCell;
  isReadOnly: boolean;
  onCommentAdded: (comment: any) => void;
}> = ({ analysis, selectedCell, isReadOnly, onCommentAdded }) => {
  const [newComment, setNewComment] = useState('');

  // Derive nodeId from selectedCell (simplified - edge extraction now in selectedCell creation)
  const nodeId = useMemo(() => {
    if (!selectedCell) {
      return null;
    }

    if (selectedCell.cellType === 'operation') {
      return selectedCell.operationNodeId;
    } else if (selectedCell.cellType === 'material') {
      return selectedCell.id;
    }

    return null;
  }, [selectedCell]);

  // Get comments for selected node (SAME FILTER AS EDITOR)
  const selectedNodeComments = useMemo((): any[] => {
    if (!nodeId) {
      return [];
    }
    const filtered = (analysis?.allComments as any[] || []).filter((c: any) => c.stepId === nodeId);

    // RunAnalysis comments from API use `createdAt` while local/editor comments
    // use `timestamp`. Normalize both so rendering stays consistent.
    return filtered.map((c: any, index: number) => ({
      ...c,
      id: c.id || c._id || `${nodeId}-comment-${index}`,
      text: c.text || c.data || c.comment || '',
      user: c.user || c.addedBy || {},
      timestamp: c.timestamp ?? c.createdAt ?? c.updatedAt ?? null,
    }));
  }, [nodeId, analysis?.allComments]);

  const handleAddComment = useCallback(() => {
    const text = newComment.trim();
    if (!text || isReadOnly || !nodeId) return;

    const comment: any = {
      id: `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      data: text,
      stepId: nodeId,
      addedBy: {
        name: 'Current User',
      },
      timestamp: Date.now(),
      type: 'ANALYSIS',
    };

    onCommentAdded(comment);
    setNewComment('');
  }, [newComment, nodeId, isReadOnly, onCommentAdded]);

  const formatDate = (timestamp?: number | string | null): string => {
    if (timestamp == null || timestamp === '') return '—';

    const date =
      typeof timestamp === 'number'
        ? new Date(timestamp)
        : new Date(String(timestamp));

    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!nodeId) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Select a step to view comments
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }}>
      {/* Comments display section */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, mb: 2 }}>
        {selectedNodeComments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No comments yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {selectedNodeComments.map((comment) => (
              <Paper
                key={comment.id}
                sx={{
                  p: 1.5,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                }}
              >
                {/* User info row: profile image + name + date/time */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Avatar
                    src="/assets/images/user-profile.png"
                    alt={comment.user?.name}
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: '0.75rem',
                      bgcolor: '#9ca3af',
                    }}
                  >
                    {(comment.user?.name || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        color: '#1f2937',
                      }}
                    >
                      {comment.user?.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#9ca3af',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDate(comment.timestamp)}
                  </Typography>
                </Box>

                {/* Comment text */}
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: '#374151',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                  }}
                >
                  {comment.text}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Input form - always visible, post button conditionally disabled */}
      <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2, mt: 'auto' }}>
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleAddComment();
          }}
        >
          {/* Textarea input */}
          <Box sx={{ mb: 1 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={4}
              placeholder={isReadOnly ? "Comments are read-only" : "Add a comment..."}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              size="small"
              variant="outlined"
              disabled={isReadOnly}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>

          {/* Post button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              size="small"
              disabled={!newComment.trim() || isReadOnly}
              sx={{
                textTransform: 'none',
                paddingX: 2,
              }}
            >
              Post
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * RunAnalysisSidebar
 *
 * Tabbed sidebar with bidirectional sync:
 * - Click step in protocol → highlights in graph + shows in info
 * - Click node in graph → highlights in protocol + shows in info
 * - Comments persist via updateRunAnalysis
 *
 * Extracts operation details from graphSnapshot for full step display.
 * Reuses editor's InfoTab component to show operation/material details.
 */
export const RunAnalysisSidebar: React.FC<RunAnalysisSidebarProps> = ({
  analysis: fullAnalysis,
}) => {
  const [activeTab, setActiveTab] = useState<TabName>('protocol');

  const { selectedNodeId, setSelectedNode } = useRunAnalysisStore();
  const { setSelection } = useGraphEditorStore();
  const selectedIds = useSelectedIds(); // Get selected cell IDs from graph

  // ── Batch scoping ─────────────────────────────────────────────────────────
  // A batched run emits one executionStep per instance, all sharing the same
  // stepId, and a graphSnapshot with one node per instance sharing the same id.
  // Unfiltered, the protocol list shows every step N times and stepId lookups
  // collapse onto whichever instance happened to be last.
  //
  // Narrow both to the instance the graph is showing (RunAnalysisGraph owns the
  // selector) and shadow the prop, so every consumer below — including
  // ProtocolTab — sees the scoped view without needing to know about batching.
  const selectedCloneNumber = useRunAnalysisStore((s) => s.selectedCloneNumber);

  const analysis = useMemo<RunAnalysisEntity>(() => {
    const full = fullAnalysis.graphSnapshot as GraphSnapshot | undefined;

    // Resolve the same way RunAnalysisGraph does, from the same store value, so
    // neither component has to own the default and the two cannot drift.
    const cloneNumber = resolveActiveClone(full, selectedCloneNumber);
    if (cloneNumber == null) return fullAnalysis;

    return {
      ...fullAnalysis,
      graphSnapshot: full ? filterSnapshotByClone(full, cloneNumber) : full,
      executionSteps: (fullAnalysis.executionSteps || []).filter(
        (step) => (step.batchInfo?.cloneNumber ?? 1) === cloneNumber,
      ),
    };
  }, [fullAnalysis, selectedCloneNumber]);

  // Get operation catalog for TranslationGenerator
  const { data: catalogList } = useOperationCatalog();

  const isReadOnly = analysis.status === 'COMPLETED' || analysis.isArchived;

  // Extract graph data from snapshot
  const snapshot = analysis.graphSnapshot as any;
  
  // Build catalog map
  const catalog = useMemo(() => {
    const map = new Map<string, OperationTypeDefinition>();
    for (const item of catalogList) {
      map.set(item.id, item);
    }
    return map;
  }, [catalogList]);
  

  
  // Convert arrays to objects keyed by ID
  // operationNodes might be an array: [ { id: "...", displayName: "..." } ]
  // We need object format: { "id-string": { id: "...", displayName: "..." } }
  const operationNodes = useMemo(() => {
    const raw = snapshot?.operationNodes || {};
    
    // If it's an array, convert to object keyed by id
    if (Array.isArray(raw)) {
      const result: any = {};
      raw.forEach((node: any) => {
        const nodeId = node.id || node.nodeId || node.operationNodeId;
        if (nodeId) {
          result[nodeId] = node;
        }
      });
      return result;
    }
    return raw;
  }, [snapshot?.operationNodes]);
  
  const materialNodes = useMemo(() => {
    const raw = snapshot?.materialNodes || {};
    
    // If it's an array, convert to object keyed by id
    if (Array.isArray(raw)) {
      const result: any = {};
      raw.forEach((node: any) => {
        const nodeId = node.id || node.nodeId || node.materialNodeId;
        if (nodeId) {
          result[nodeId] = node;
        }
      });
      return result;
    }
    return raw;
  }, [snapshot?.materialNodes]);
  
  const flowEdges = snapshot?.flowEdges || {};
  const groups = snapshot?.groups || {};



  // Map executionSteps to stepIds for faster lookup
  const stepIdMap = useMemo(() => {
    const map = new Map<string, any>();
    (analysis.executionSteps || []).forEach((step) => {
      map.set(step.stepId, step);
    });
    return map;
  }, [analysis.executionSteps]);

  // Create reverse mapping: graphNodeId → stepId
  // This is crucial because selectedIds from graph are graphNodeIds, not stepIds!
  const graphNodeIdToStepId = useMemo(() => {
    const map = new Map<string, string>();
    
    if (!snapshot) {
      return map;
    }
    
    // For each execution step, find which operation node corresponds to it
    (analysis.executionSteps || []).forEach((step: any) => {
      // Search through operation nodes
      for (const [nodeId, operationNode] of Object.entries(operationNodes)) {
        const node = operationNode as any;
        
        // Match by step.stepId
        if (step.stepId === nodeId) {
          map.set(nodeId, step.stepId);
          return;
        }
        
        // Match by operationNodeId field
        if (step.operationNodeId === nodeId) {
          map.set(nodeId, step.stepId);
          return;
        }
        
        // Match by displayName
        if (node.displayName === step.displayName) {
          map.set(nodeId, step.stepId);
        }
      }
    });
    
    return map;
  }, [snapshot, analysis.executionSteps, operationNodes]);

  // Note: No longer need to map flowEdges since we parse edge cell IDs directly from graph selection

  // Derive highlighted step IDs from graph selection
  // When user clicks graph node, convert graphNodeId → stepId using the mapping
  const highlightedStepIds = useMemo(() => {
    const highlighted = new Set<string>();

    for (const selectedId of selectedIds) {
      // Case 1: Direct operation node ID
      const stepId = graphNodeIdToStepId.get(selectedId);
      if (stepId) {
        highlighted.add(stepId);
        continue;
      }

      // Case 2: Edge cell ID format from graph (op-edge-UUID or op-edge-UUID-suffix)
      if (typeof selectedId === 'string' && selectedId.startsWith('op-edge-')) {
        let operationId = selectedId.substring('op-edge-'.length);
        
        // Remove known transfer step suffixes
        const suffixes = ['-transfer', '-remainder', '-dest'];
        for (const suffix of suffixes) {
          if (operationId.endsWith(suffix)) {
            operationId = operationId.substring(0, operationId.length - suffix.length);
            break;
          }
        }
        
        // Now try to map this operation ID to a step
        const connectedStepId = graphNodeIdToStepId.get(operationId);
        if (connectedStepId) {
          highlighted.add(connectedStepId);
          continue;
        }
      }

      // Case 3: Fallback - maybe the selectedId IS a stepId (direct from analysis store)
      if (stepIdMap.has(selectedId)) {
        highlighted.add(selectedId);
      }
    }

    return highlighted;
  }, [selectedIds, graphNodeIdToStepId, stepIdMap]);



  // Create reverse mapping: stepId → graphNodeId
  // Used when clicking a step to select the corresponding graph node
  const stepIdToGraphNodeId = useMemo(() => {
    const map = new Map<string, string>();
    
    for (const [graphNodeId, stepId] of graphNodeIdToStepId.entries()) {
      map.set(stepId, graphNodeId);
    }
    
    return map;
  }, [graphNodeIdToStepId]);

  // Auto-sync: when graph selection changes, update analysis store
  useEffect(() => {
    if (highlightedStepIds.size > 0) {
      // Operation node(s) selected → sync first one to store
      const firstHighlightedId = Array.from(highlightedStepIds)[0];
      if (firstHighlightedId !== selectedNodeId) {
        setSelectedNode(firstHighlightedId);
      }
    } else if (selectedIds.length > 0) {
      // No operation selected, check if material or edge is selected
      const firstSelectedId = selectedIds[0];
      
      // Material node
      if (materialNodes[firstSelectedId]) {
        if (firstSelectedId !== selectedNodeId) {
          setSelectedNode(firstSelectedId);
        }
      }
      // Edge cell ID (op-edge-*)
      else if (typeof firstSelectedId === 'string' && firstSelectedId.startsWith('op-edge-')) {
        if (firstSelectedId !== selectedNodeId) {
          setSelectedNode(firstSelectedId);
        }
      }
    }
  }, [selectedIds, highlightedStepIds, selectedNodeId, setSelectedNode, materialNodes]);

  // Create selectedCell from selectedNodeId for editor's InfoTab
  // Handle both operation nodes (mapped from steps) and material nodes (direct)
  const selectedCell: SelectedCell = useMemo(() => {
    if (!selectedNodeId) {
      return null;
    }
    
    // Try mapping: selectedNodeId (as stepId) → graphNodeId
    const mappedGraphNodeId = stepIdToGraphNodeId.get(selectedNodeId);
    
    // Use mapped ID if found, otherwise use selectedNodeId directly as graphNodeId
    const graphNodeId = mappedGraphNodeId || selectedNodeId;
    
    // Check material node first
    if (materialNodes[graphNodeId]) {
      return { cellType: 'material', id: graphNodeId };
    }
    
    // Check operation node
    if (operationNodes[graphNodeId]) {
      return {
        cellType: 'operation',
        operationNodeId: graphNodeId,
        label: operationNodes[graphNodeId].displayName,
      };
    }
    
    // Check if it's a group
    if (groups[graphNodeId]) {
      return { cellType: 'group', id: graphNodeId };
    }
    
    // Check if it's an edge cell ID (format: op-edge-UUID-suffix or op-edge-UUID)
    // Visual operation edge IDs (op-edge-*) map back to operation details in Info tab (like workflow)
    if (typeof graphNodeId === 'string') {
      const operationId = extractOperationIdFromEdge(graphNodeId);
      if (operationId && operationNodes[operationId]) {
        return {
          cellType: 'operation',
          operationNodeId: operationId,
          label: operationNodes[operationId].displayName,
        };
      }
    }
    
    // Not found in any map
    return null;
  }, [selectedNodeId, stepIdToGraphNodeId, materialNodes, operationNodes, groups]);

  const handleEditRequested = useCallback((nodeId: string) => {
    // Read-only for completed/archived analyses
    if (isReadOnly) return;

  }, [isReadOnly]);

  return (
    <Box
      sx={{
        width: 350,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderLeft: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Tab headers */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.default',
          flexShrink: 0,
          '& .MuiTabs-indicator': {
            backgroundColor: 'primary.main',
          },
        }}
      >
        <Tab label="Protocol" value="protocol" />
        <Tab label="Comments" value="comments" />
        <Tab label="Info" value="info" />
      </Tabs>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {activeTab === 'protocol' && (
          <ProtocolTab 
            analysis={analysis} 
            highlightedStepIds={highlightedStepIds}
            operationNodesMap={new Map(
              ((analysis.executionSteps || []).map((step: any): [string, any] => 
                [step.stepId, operationNodes[stepIdToGraphNodeId.get(step.stepId) || '']]
              ).filter(([, op]) => op)) as [string, any][]
            )}
            stepIdToGraphNodeId={stepIdToGraphNodeId}
            graphNodeIdToStepId={graphNodeIdToStepId}
            catalog={catalog}
            snapshot={snapshot}
            materialNodes={materialNodes}
            operationNodes={operationNodes}
            flowEdges={flowEdges}
            groups={groups}
            workspaceId={analysis.workspaceId}
          />
        )}
        {activeTab === 'comments' && (
          <CommentsTab 
            analysis={analysis} 
            selectedCell={selectedCell}
            isReadOnly={true}
            onCommentAdded={() => {}}
          />
        )}
        {activeTab === 'info' && (
          <Box sx={{ overflowY: 'auto', height: '100%' }}>
            {!selectedCell ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', mt: 4 }}>
                <Typography variant="body2">
                  Click a node or edge on the canvas to see details.
                </Typography>
              </Box>
            ) : selectedCell.cellType === 'operation' ? (
              // Use OperationInfo from editor for consistent formatting
              (() => {
                const operation = operationNodes[selectedCell.operationNodeId];
                if (!operation) {
                  return (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body2">Operation not found</Typography>
                    </Box>
                  );
                }
                
                // Calculate input and output materials from flowEdges
                const inputMats = Object.values(flowEdges || {})
                  .filter((e: any) => e.targetId === selectedCell.operationNodeId && materialNodes[e.sourceId])
                  .map((e: any) => ({ id: e.sourceId, ...materialNodes[e.sourceId] }));
                
                const outputMats = Object.values(flowEdges || {})
                  .filter((e: any) => e.sourceId === selectedCell.operationNodeId && materialNodes[e.targetId])
                  .map((e: any) => ({ id: e.targetId, ...materialNodes[e.targetId] }));
                
                return (
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 2 }}>
                      Operation: {operation.displayName || selectedCell.label || 'Operation'}
                    </Typography>
                    <OperationInfo
                      data={{
                        id: selectedCell.operationNodeId,
                        ...operation,
                        dataExpected: resolveDataExpected(operation),
                      }}
                      inputMaterials={inputMats}
                      outputMaterials={outputMats}
                    />
                  </Box>
                );
              })()
            ) : selectedCell.cellType === 'material' ? (
              // Use MaterialInfo from editor for consistent formatting
              (() => {
                const material = materialNodes[selectedCell.id];
                if (!material) {
                  return (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body2">Material not found</Typography>
                    </Box>
                  );
                }
                
                return (
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 2 }}>
                      Material: {material.name || 'Material'}
                    </Typography>
                    <MaterialInfo data={{ id: selectedCell.id, ...material }} />
                  </Box>
                );
              })()
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                  {selectedCell.cellType === 'group' ? 'Group' : 'Selection'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedCell.cellType} selected
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
